import {
    ACard,
    ADataGrid,
    ADataGridColumn,
} from "@atiautomacao/ati-ui-library";
import React, {FunctionComponent, useState} from "react";
import {
    Box,
    Divider,
    Stack,
    TextField, Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {InfoOutlined, SimCardDownloadOutlined} from "@mui/icons-material";
import UnavailabilityBarChart from "./UnavailabilityBarChart";
import {FindUnavailabilityPowerStationsReactQuery} from "../reactQuery/UnavailabilityReactQuery";
import CloseIcon from "@mui/icons-material/Close";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, {Dayjs} from "dayjs";
import {useSnackbar} from "notistack";
import {formatNumberWithMaxTwoDecimals} from "../../../../Utils/NumberUtil";
import themeDefaults from "../../../../Shared/Theme/ThemeDefaults";
import {UnavailabilityPowerStation} from "../../../../Config/Types";
import UnavailabilityModeToggle from "./UnavailabilityModeToggle";
import {useUnavailabilityLossesExport} from "../hooks/useUnavailabilityLossesExport";
import {
    aggregateUnavailabilityByStation,
    buildLossTable,
    CHART_SERIES,
    CHART_TOOLTIP,
    DIN_TABLE_COLUMNS,
    hasUnavailabilityLoss,
    LossTable,
    LossTableRow,
    UNAVAILABILITY_CHART_TITLE,
    UNAVAILABILITY_CHART_TITLE_ICON,
    UnavailabilityChartMode
} from "../utils/UnavailabilityChartUtils";

const allBreakpoints = {
    xs: true,
    sm: true,
    md: true,
    lg: true,
    xl: true
};

const buildColumn = (name: string, label: string, width?: number): ADataGridColumn => ({
    name,
    label,
    align: 'left',
    ...(width ? {width} : {}),
    minWidth: width ?? 100,
    visibility: true,
    breakpoints: allBreakpoints,
} as ADataGridColumn);

// Tabela da visualização Totais (DIN x DEX): mantém as colunas anteriores à SGD-3815 (RN2.d).
export const columns: ADataGridColumn[] = [
    buildColumn('position', '#', 10),
    {...buildColumn('station', 'Usina'), minWidth: 10},
    ...CHART_SERIES.TOTAL.map(item => buildColumn(item.key, item.columnLabel)),
    buildColumn('total', 'Total (kWh)'),
    buildColumn('totalPerCent', '% do total'),
];

// Tabela detalhada da visualização DIN: sem a coluna de posição e com a linha Total fixa no rodapé.
export const dinColumns: ADataGridColumn[] = DIN_TABLE_COLUMNS.map(column => buildColumn(column.key, column.label));

const formatKwh = (value: number) => String(formatNumberWithMaxTwoDecimals(value));

const toDinGridRow = (row: LossTableRow): Record<string, string | number> => ({
    id: row.id,
    station: row.station,
    ...Object.fromEntries(CHART_SERIES.DIN.map(item => [item.key, formatKwh(row.values[item.key] ?? 0)])),
    total: formatKwh(row.total),
    impact: `${row.impact.toFixed(2)}%`,
});

export const buildDinGridRows = (table: LossTable) => ({
    rows: table.rows.map(toDinGridRow),
    footerRows: [toDinGridRow(table.totalRow)],
});

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    bgcolor: 'background.paper',
    //border: '1px solid #666',
     borderRadius: '25px'
}

interface UnavailabilityRow {
    id: number | string,
    position: number,
    station: string,
    total: number | string,
    totalPerCent: string,
    [seriesKey: string]: number | string
}

export const MAX_RANGE_DAYS = 180;

export function validateDateRange(
    start: Dayjs | string | null,
    end: Dayjs | string | null,
    changedField: 'start' | 'end',
    maxRangeDays: number = MAX_RANGE_DAYS,
): string | null {
    const startDate = start ? dayjs(start) : null;
    const endDate = end ? dayjs(end) : null;

    if (!startDate?.isValid() || !endDate?.isValid()) {
        return null;
    }

    const diffDays = endDate.diff(startDate, 'day');

    if (diffDays < 0) {
        return changedField === 'start'
            ? 'A data inicial não pode ser maior que a data final.'
            : 'A data final não pode ser menor que a data inicial.';
    }

    if (diffDays > maxRangeDays) {
        return `O período máximo permitido é de ${maxRangeDays} dias.`;
    }

    return null;
}

interface ExpandedUnavailabilityChartProps {
    startDateTime: string;
    endDateTime: string;
    chartMode: UnavailabilityChartMode;
    onChartModeChange: (mode: UnavailabilityChartMode) => void;
    onClose: () => void;
}

export const buildRows = (data: UnavailabilityPowerStation[] | undefined): UnavailabilityRow[] => {
    const series = CHART_SERIES.TOTAL;
    const stations = aggregateUnavailabilityByStation(data, series.map(item => item.key));
    const maxValue = stations.reduce((acc, item) => acc + item.total, 0);
    const formatValue = (value: number) => value ? formatNumberWithMaxTwoDecimals(value) : '0';

    return stations.map((station, index) => ({
        id: station.id,
        position: index + 1,
        station: station.name,
        ...Object.fromEntries(series.map(item => [item.key, formatValue(station.values[item.key])])),
        total: formatValue(station.total),
        totalPerCent: maxValue === 0 ? '0%' : (station.total / maxValue * 100).toFixed(2) + '%'
    }));
};

const ExpandedUnavailabilityChart: FunctionComponent<ExpandedUnavailabilityChartProps> = ({startDateTime, endDateTime, chartMode, onChartModeChange, onClose}) => {
    const [rangeStartISO, setRangeStartISO] = useState<string | null>(startDateTime);
    const [rangeEndISO, setRangeEndISO] = useState<string | null>(endDateTime);
    const {data, dataMode, isError} = FindUnavailabilityPowerStationsReactQuery(rangeStartISO, rangeEndISO, chartMode)
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    // Exporta com o período do próprio modal e a visualização selecionada.
    const handleExport = useUnavailabilityLossesExport(rangeStartISO, rangeEndISO, chartMode);

    const handleOnColor = (data:any, columnItem: any) => {
        const colorMap: Record<string, string> = {
            ...Object.fromEntries(CHART_SERIES[dataMode].map(item => [item.key, item.tableColor ?? item.color])),
            // text.secondary equivale ao #00000099 no tema light e fica legível no dark.
            totalPerCent: theme.palette.text.secondary,
            impact: theme.palette.text.secondary,
        };

        return {
            color: colorMap[columnItem.name] || '',
            backgroundColor: '',
        };
    }

    const handleRangeStartChange = (newValue: Dayjs | null) => {
        if (!newValue || !newValue.isValid()) {
            enqueueSnackbar("Informe uma data válida no campo Data inicial.", { variant: 'error' });
            return;
        }

        const error = validateDateRange(newValue, rangeEndISO, 'start');
        if (error) {
            enqueueSnackbar(error, { variant: 'warning' });
            return;
        }
        const date = newValue?.isValid() ? newValue.startOf("day").format("YYYY-MM-DDTHH:mm:ss") : null;
        setRangeStartISO(date);
    };

    const handleRangeEndChange = (newValue: Dayjs | null) => {
        if (!newValue || !newValue.isValid()) {
            enqueueSnackbar("Informe uma data válida no campo Data final.", { variant: 'error' });
            return;
        }

        const error = validateDateRange(rangeStartISO, newValue, 'end');
        if (error) {
            enqueueSnackbar(error, { variant: 'warning' });
            return;
        }
        const date = newValue?.isValid() ? newValue.endOf("day").format("YYYY-MM-DDTHH:mm:ss") : null;
        setRangeEndISO(date);
    };

    const seriesKeys = React.useMemo(() => CHART_SERIES[dataMode].map(item => item.key), [dataMode]);
    // Todas as usinas, sem o agrupamento "Outras" do gráfico, com os mesmos valores das barras.
    const stations = React.useMemo(() => aggregateUnavailabilityByStation(data, seriesKeys), [data, seriesKeys]);
    const hasData = hasUnavailabilityLoss(stations);
    const totalRows = React.useMemo(() => dataMode === 'TOTAL' ? buildRows(data) : [], [data, dataMode]);
    const dinGrid = React.useMemo(
        () => dataMode === 'DIN' ? buildDinGridRows(buildLossTable(stations, seriesKeys)) : null,
        [dataMode, stations, seriesKeys]
    );

    return (
        <Box sx={style}>
        <ACard
            ariaLabel={"din-dex-unavailability"}
            key={`chart-power-irradiance`}
            styleProps={{
                cardStyle: { minHeight: 500, maxHeight: hasData ? 800 : 500},
                contentStyle: { padding: 0 },
                headerStyle: {paddingTop: 2},
                headerActionsStyle: {marginLeft: 3, marginTop: 0},
                titleStyle: {marginTop: 0}
            }}
            headerControlsPosition={"header"}
            title={UNAVAILABILITY_CHART_TITLE}
            iconTitle={UNAVAILABILITY_CHART_TITLE_ICON}
            headerControls={
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                     <Stack sx={{marginBottom: 1}} direction="row" spacing={2} alignItems="center">
                            <UnavailabilityModeToggle value={chartMode} onChange={onChartModeChange}/>
                            <DatePicker
                                disabled={true}
                                label="Data inicial"
                                inputFormat="DD/MM/YYYY"
                                value={rangeStartISO}
                                onChange={handleRangeStartChange}
                                renderInput={(params: any) => <TextField size="small" sx={{ width: 170 }} {...params} />}
                            />
                            <DatePicker
                                disabled={true}
                                label="Data final"
                                inputFormat="DD/MM/YYYY"
                                value={rangeEndISO}
                                onChange={handleRangeEndChange}
                                renderInput={(params: any) => <TextField size="small" sx={{ width: 170 }} {...params} />}
                            />
                     </Stack>
                </LocalizationProvider>
            }
            headerActions={
                <Stack sx={{width: '100%', marginRight: 6}} direction="column" spacing={"2px"}>
                    <Box onClick={() => handleExport("csv")} sx={{ width: '100%', display: 'flex',alignItems: 'center', justifyContent: 'space-between', padding: '0 5px'}}>
                        <IconButton size="small"  sx={{borderRadius: '4px', width: '100%', justifyContent: 'flex-start'}}>
                            <SimCardDownloadOutlined sx={{marginRight: '12px'}}/>
                            <Typography variant="body2">Baixar como CSV</Typography>
                        </IconButton>
                    </Box>
                    <Divider />
                    <Box onClick={() => handleExport("excel")} sx={{ width: '100%', display: 'flex',alignItems: 'center', justifyContent: 'space-between', padding: '0 5px'}}>
                        <IconButton size="small"  sx={{borderRadius: '4px', width: '100%', justifyContent: 'flex-start'}}>
                            <SimCardDownloadOutlined sx={{marginRight: '12px'}}/>
                            <Typography variant="body2">Baixar como Excel</Typography>
                        </IconButton>
                    </Box>
                    <Divider />
                </Stack>
            }
            InformationTooltip={
                <Stack direction="row" spacing={"2px"} alignItems="center">
                    <Tooltip describeChild title={CHART_TOOLTIP[dataMode]} arrow placement={'top-end'}>
                        <InfoOutlined fontSize='small' style={{ backgroundColor: themeDefaults.colors.primary,color: 'white', borderRadius: '50%', border: '1px solid rgba(45, 93, 231, 1)',}}/>
                    </Tooltip>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            }

        >
            <UnavailabilityBarChart data={data} mode={dataMode} isError={isError} isExpansive={true} height={350}/>
            {hasData && (
                <>
                <Divider sx={{marginTop: 1}}/>
                <Box sx={{paddingRight: 3, paddingLeft: 3, marginBottom: 2}}>
                    <Box sx={{
                        justifyContent: 'center',
                        marginTop: 1
                    }}>
                        <Typography
                            sx={{ fontWeight: 600, color: isDarkMode ? theme.palette.text.primary : '#333', fontSize: '0.8rem' }}
                        >
                            TABELA DE DADOS COMPLETA
                        </Typography>
                    </Box>
                    <ADataGrid
                        maxHeight={300}
                        hideSelection={true}
                        ariaLabel={dinGrid ? 'din-losses-table' : 'din-dex-full-table'}
                        hideFilters={true}
                        size={"small"}
                        columns={dinGrid ? dinColumns : columns}
                        rows={dinGrid ? dinGrid.rows : totalRows}
                        footerRows={dinGrid?.footerRows}
                        onColorSelect={handleOnColor}
                        tableContainerStyle={{borderRadius: 0, marginTop: 1, marginBottom: 0}}
                        headerStyle={{
                            // No modo dark o fundo claro fixo deixava o texto do cabeçalho ilegível.
                            color: isDarkMode ? theme.palette.text.primary : '#626262',
                            backgroundColor: isDarkMode ? theme.palette.background.paper : '#F5F5F5',
                            borderBottom: `2px solid ${isDarkMode ? theme.palette.divider : '#e0e0e0'}`,
                            textTransform: 'capitalize',
                            margin: 0,
                            fontSize: '7px !important',
                        }}
                        page={1}
                        loading={false}
                        totalOfRecords={1}
                        rowsPerPage={15}
                        showSortOptions={false}
                        disablePagination={true}
                    />
                </Box>
                </>
            )}
        </ACard>
        </Box>
    );
}

export default ExpandedUnavailabilityChart;
